import { Divider, Loading, MobileModal } from '@byecode/ui'
import { type DataSourceAbstract, type Field, type RelativeSelectConfig } from '@lighthouse/core'
import { useDebounce } from '@lighthouse/tools'
import { type DrawerProps } from '@mantine/core'
import { useElementSize } from '@mantine/hooks'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useUpdateEffect } from 'react-use'

import { useFieldBlockContext } from '../../../../../contexts'
import { useAtomData } from '../../../../../hooks'
import { FieldTableMobile } from '../../../../FiledSelectTable'
import type { RelativeFieldParam } from '../../../types'
import { AddButton } from '../AddButton'
import FieldList from '../FieldList'
import { SearchInput } from '../SearchInput'
import { useRelativeDatasource } from '../useRelativeDatasource'
import * as SC from './styles'

interface RelativeSelectDrawerProps
    extends Pick<RelativeFieldParam, 'onFetchDataSource' | 'dataSourceList' | 'onLoadMoreData' | 'onAddClickEvent'> {
    id: string
    dsId: string
    dataSource: DataSourceAbstract | null
    value?: string[]
    isMultiple?: boolean
    title: string
    target?: DrawerProps['target']
    relativeSelectConfig?: RelativeSelectConfig
    opened: boolean
    style?: React.CSSProperties
    onChange?: (val: string[]) => void
    onClose: () => void
}

export const RelativeSelectDrawer: React.FunctionComponent<RelativeSelectDrawerProps> = ({
    id,
    dsId,
    dataSource,
    opened,
    value = [],
    title,
    isMultiple,
    target,
    relativeSelectConfig,
    dataSourceList,
    style,
    onChange,
    onClose,
    onFetchDataSource,
    onLoadMoreData,
    onAddClickEvent
}) => {
    const { schema, viewOptions } = dataSource ?? {}
    const { pagination, tableProps: fieldProps = [] } = viewOptions ?? {}

    const { relativeSelect } = relativeSelectConfig ?? {}
    const { showType = 'table', addButton } = relativeSelect ?? {}
    const { stackId, viewIndependentDataAtomFamily } = useFieldBlockContext()
    const viewIndependentData = useAtomData(viewIndependentDataAtomFamily({ stackId, viewId: id, dsId }))
    const { rowTotal = 0, recordPoints = [], currentPage = 1 } = viewIndependentData ?? {}
    const currentPageRef = useRef(currentPage)
    const {
        state: { loading, keywords, initLoading },
        handleFetchData,
        handleChangeSearch,
        handleLoadMoreData,
        handleChangeInitLoading
    } = useRelativeDatasource({ id, onFetchDataSource, onLoadMoreData, dsId })

    const containerRef = useRef<HTMLDivElement | null>(null)

    const { ref: scrollRef, height } = useElementSize<HTMLDivElement>()

    const columns = useMemo(() => {
        if (!schema) {
            return []
        }
        if (fieldProps && fieldProps.length > 0) {
            return fieldProps.reduce<Field[]>((cols, col) => {
                if (schema[col.id] && col.visible) {
                    cols.push(schema[col.id])
                }
                return cols
            }, [])
        }
        return Object.entries(schema).reduce<Field[]>((cols, [key, col]) => {
            cols.push({ ...col })
            return cols
        }, [])
    }, [fieldProps, schema])

    const debouncedKeywords = useDebounce(keywords, 500)

    useUpdateEffect(() => {
        opened && handleFetchData(debouncedKeywords)
    }, [debouncedKeywords, opened])

    const handleScrollPositionChange = useCallback(
        async (position: { x: number; y: number }) => {
            const { y } = position
            if (!scrollRef.current || loading || currentPageRef.current > currentPage) {
                return
            }
            const { clientHeight, scrollHeight } = scrollRef.current
            const isBottom = y + clientHeight + 100 > scrollHeight

            if (isBottom && rowTotal > recordPoints.length) {
                currentPageRef.current = currentPage + 1
                await handleLoadMoreData(currentPageRef.current)
            }
        },
        [currentPage, handleLoadMoreData, loading, recordPoints.length, rowTotal, scrollRef]
    )

    const invokeChange = useCallback(
        (val: string[]) => {
            onChange?.(val)
        },
        [onChange]
    )

    const handleItemClick = useCallback(
        (outputValue: string) => {
            const isExists = value.includes(outputValue)
            let newValue: string[] = []
            if (isMultiple) {
                newValue = isExists ? value.filter(item => item !== outputValue) : [...value, outputValue]
                invokeChange(newValue)
                return
            }
            newValue = isExists ? [] : [outputValue]
            invokeChange(newValue)
            onClose()
        },
        [value, isMultiple, invokeChange, onClose]
    )

    useEffect(() => {
        if (opened) {
            handleChangeInitLoading(true)
            handleFetchData?.()
        }
        handleChangeSearch('')
    }, [handleChangeInitLoading, handleChangeSearch, handleFetchData, opened])

    return (
        <MobileModal
            data-ignore-click-away
            target={target}
            title={title}
            open={opened}
            styles={{
                modal: {
                    height: '85%'
                },
                body: {
                    paddingBottom: addButton?.enable ? '0px!important' : undefined,
                    backgroundColor: showType === 'list' ? 'var(--color-gray-50)' : '#fff'
                }
            }}
            style={style}
            onClose={onClose}
        >
            <SC.RecordSelector ref={containerRef}>
                <SearchInput
                    value={keywords}
                    autoFocus={false}
                    showType="list"
                    style={{ backgroundColor: 'transparent', borderBottom: 'none' }}
                    onChange={handleChangeSearch}
                />
                {initLoading ? (
                    <Loading />
                ) : showType === 'table' ? (
                    <FieldTableMobile
                        ref={scrollRef}
                        dsId={dsId}
                        value={value}
                        columns={columns}
                        loading={loading}
                        primaryField="ID"
                        recordPoints={recordPoints}
                        dataSourceList={dataSourceList || []}
                        onRowClick={handleItemClick}
                        onScrollPositionChange={handleScrollPositionChange}
                    />
                ) : (
                    <FieldList
                        recordPoints={recordPoints}
                        value={value}
                        ref={scrollRef}
                        isDriver
                        dataSource={dataSource}
                        onScrollPositionChange={handleScrollPositionChange}
                        onChange={handleItemClick}
                        style={{
                            border: '1px solid var(--color-gray-200)',
                            background: '#fff',
                            borderRadius: 8
                        }}
                    />
                )}
                {addButton?.enable && addButton && (
                    <>
                        <Divider />
                        <AddButton
                            id={id}
                            style={{ height: 48, margin: 0, justifyContent: 'center', flexShrink: 0 }}
                            config={addButton}
                            onClose={onClose}
                            onClickEvent={onAddClickEvent}
                        />
                    </>
                )}
            </SC.RecordSelector>
        </MobileModal>
    )
}
